import React, { useState } from 'react';
import { Upload, Archive, Trash2, CheckCircle2, Loader2, Images, Sliders, HardDrive, Maximize2 } from 'lucide-react';
import { createZipBlob } from '../../utils/zipWriter';
import { processImage } from '../../utils/imageProcessing';
import { formatFileSize } from '../../utils/fileSize';

interface BatchFileItem {
  id: string;
  file: File;
  previewUrl: string;
  status: 'pending' | 'processing' | 'done' | 'error';
  processedDataUrl?: string;
  errorMsg?: string;
}

interface BatchUploaderProps {
  outputFormat?: string;
  outputQuality?: number;
  targetWidth?: number;
  targetHeight?: number;
  targetKbSize?: number;
}

export function BatchUploader({
  outputFormat: initialFormat = 'image/jpeg',
  outputQuality: initialQuality = 85,
  targetWidth: initialWidth,
  targetHeight: initialHeight,
  targetKbSize: initialKb,
}: BatchUploaderProps) {
  const [items, setItems] = useState<BatchFileItem[]>([]);
  const [isProcessingAll, setIsProcessingAll] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  // Interactive controls state
  const [batchFormat, setBatchFormat] = useState<string>(initialFormat || 'image/jpeg');
  const [batchQuality, setBatchQuality] = useState<number>(initialQuality || 85);
  const [widthInput, setWidthInput] = useState<string>(initialWidth ? String(initialWidth) : '');
  const [heightInput, setHeightInput] = useState<string>(initialHeight ? String(initialHeight) : '');
  const [targetKbInput, setTargetKbInput] = useState<string>(initialKb ? String(initialKb) : '');
  const [maintainAspect, setMaintainAspect] = useState<boolean>(true);

  const handleFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);

    const newItems: BatchFileItem[] = newFiles.map((file) => ({
      id: Math.random().toString(36).substring(2, 9),
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'pending',
    }));

    setItems((prev) => [...prev, ...newItems]);
  };

  const removeItem = (id: string) => {
    setItems((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });
  };

  const clearAll = () => {
    items.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    setItems([]);
  };

  const processAllBatch = async () => {
    if (items.length === 0) return;
    setIsProcessingAll(true);

    const numericWidth = widthInput ? parseInt(widthInput, 10) : undefined;
    const numericHeight = heightInput ? parseInt(heightInput, 10) : undefined;
    const numericKb = targetKbInput ? parseInt(targetKbInput, 10) : undefined;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      setItems((prev) =>
        prev.map((it) => (it.id === item.id ? { ...it, status: 'processing' } : it))
      );

      try {
        const processedUrl = await processImage(item.file, {
          resize: (numericWidth || numericHeight) ? {
            width: numericWidth || 0,
            height: numericHeight || 0,
            maintainAspectRatio: maintainAspect
          } : undefined,
          output: {
            format: batchFormat || 'image/jpeg',
            quality: batchQuality || 85,
            targetSize: numericKb,
          },
        });

        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id ? { ...it, status: 'done', processedDataUrl: processedUrl } : it
          )
        );
      } catch (err) {
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id
              ? { ...it, status: 'error', errorMsg: err instanceof Error ? err.message : 'Error' }
              : it
          )
        );
      }
    }

    setIsProcessingAll(false);
  };

  const downloadAllZip = async () => {
    let processedItems = items.filter((it) => it.status === 'done' && it.processedDataUrl);
    
    // Auto process if user clicks download before processing
    if (processedItems.length === 0 && items.length > 0) {
      await processAllBatch();
      processedItems = items.filter((it) => it.status === 'done' && it.processedDataUrl);
    }

    if (processedItems.length === 0) return;

    setDownloadingZip(true);
    try {
      const fmt = batchFormat || initialFormat || 'image/jpeg';
      const ext = (fmt.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const filesToZip = processedItems.map((it, idx) => ({
        name: `resized-${idx + 1}-${it.file.name.replace(/\.[^/.]+$/, '')}.${ext}`,
        dataUrl: it.processedDataUrl!,
      }));

      const zipBlob = await createZipBlob(filesToZip);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `batch-resized-images-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to create ZIP:', err);
    } finally {
      setDownloadingZip(false);
    }
  };

  const completedCount = items.filter((it) => it.status === 'done').length;

  return (
    <div className="space-y-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 sm:p-7 shadow-xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div>
          <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Images className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Batch Multi-Image Resizer & Converter
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Bulk resize, compress, and convert photos simultaneously, then download as a ZIP archive.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs font-bold rounded-xl cursor-pointer transition-all shadow-md inline-flex items-center gap-1.5 hover:scale-105">
            <Upload className="w-4 h-4" />
            <span>Select Photos</span>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFilesSelect}
              className="hidden"
            />
          </label>
          {items.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="px-3 py-2.5 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold rounded-xl transition-colors"
            >
              Clear Queue
            </button>
          )}
        </div>
      </div>

      {/* Interactive Control Panel for End Users */}
      <div className="bg-slate-50 dark:bg-slate-950 p-4 sm:p-5 rounded-xl border border-slate-200 dark:border-slate-800 space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <Sliders className="w-4 h-4 text-indigo-500" />
          Batch Resizing & Conversion Controls
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Format Selection */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Format</label>
            <select
              value={batchFormat}
              onChange={(e) => setBatchFormat(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-medium text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="image/jpeg">JPG / JPEG</option>
              <option value="image/png">PNG</option>
              <option value="image/webp">WEBP</option>
            </select>
          </div>

          {/* Width & Height */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Target Dimensions (px)</label>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                placeholder="Width"
                value={widthInput}
                onChange={(e) => setWidthInput(e.target.value)}
                className="w-full px-2.5 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-medium text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-slate-400 text-xs">×</span>
              <input
                type="number"
                placeholder="Height"
                value={heightInput}
                onChange={(e) => setHeightInput(e.target.value)}
                className="w-full px-2.5 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-medium text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Quality Slider */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
              <span>Quality</span>
              <span className="font-mono text-indigo-600 dark:text-indigo-400">{batchQuality}%</span>
            </div>
            <input
              type="range"
              min="10"
              max="100"
              value={batchQuality}
              onChange={(e) => setBatchQuality(Number(e.target.value))}
              className="w-full accent-indigo-600 cursor-pointer h-2 mt-2"
            />
          </div>

          {/* Target File Size (KB) */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Max File Size (KB, optional)</label>
            <input
              type="number"
              placeholder="e.g. 500"
              value={targetKbInput}
              onChange={(e) => setTargetKbInput(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-medium text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={maintainAspect}
              onChange={(e) => setMaintainAspect(e.target.checked)}
              className="accent-indigo-600 rounded"
            />
            <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Lock Aspect Ratio</span>
          </label>
        </div>
      </div>

      {/* Queue & Process */}
      {items.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-10 text-center bg-slate-50/50 dark:bg-slate-950/30 space-y-3">
          <Images className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto" />
          <div className="space-y-1">
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300">No images added to batch queue</p>
            <p className="text-xs text-slate-400">Select multiple photos to resize, compress, and convert in bulk</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-[360px] overflow-y-auto pr-1">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 relative group"
              >
                <img
                  src={item.previewUrl}
                  alt={item.file.name}
                  className="w-12 h-12 rounded-lg object-cover border border-slate-200 dark:border-slate-800 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">{item.file.name}</p>
                  <p className="text-[11px] text-slate-500 font-mono">{formatFileSize(item.file.size)}</p>
                  <div className="mt-1 flex items-center gap-1 text-[11px]">
                    {item.status === 'pending' && <span className="text-slate-400 font-medium">Pending</span>}
                    {item.status === 'processing' && (
                      <span className="text-indigo-600 dark:text-indigo-400 flex items-center gap-1 font-semibold">
                        <Loader2 className="w-3 h-3 animate-spin" /> Processing...
                      </span>
                    )}
                    {item.status === 'done' && (
                      <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Ready
                      </span>
                    )}
                    {item.status === 'error' && <span className="text-red-500 font-semibold">{item.errorMsg || 'Failed'}</span>}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg transition-colors"
                  title="Remove image"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <span className="text-xs text-slate-600 dark:text-slate-400 font-bold">
              Queue: {completedCount} / {items.length} completed
            </span>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={processAllBatch}
                disabled={isProcessingAll || items.length === 0}
                className="flex-1 sm:flex-none px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-md inline-flex items-center justify-center gap-1.5 hover:scale-105"
              >
                {isProcessingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Process All Images
              </button>

              <button
                type="button"
                onClick={downloadAllZip}
                disabled={downloadingZip || items.length === 0}
                className="flex-1 sm:flex-none px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-md inline-flex items-center justify-center gap-1.5 hover:scale-105"
              >
                {downloadingZip ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                Download ZIP Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
